/**
 * The ONE answer to "when is this federal-tax filing due?" (spec 2026-09-26,
 * section 7.4). Moved here from the parked `federal-tax-audit-fixes` branch
 * (`apps/api/src/federal-tax/filing-deadline.ts`, commit 41a655165) so the API's
 * triage queue and deadline list, the console and the customer app share it.
 *
 * Rules:
 *
 * - The base is the order's scenario and tax year. The filing's deadline is the
 *   earliest due date among the scenario's forms (`formDeadlines`): Form 1065
 *   for an MMLLC (Mar 15, extended Sep 15); Form 5472 / 1120 for SMLLCs and
 *   C-corps (Apr 15, extended Oct 15); an SMLLC with ECI also files a 1040-NR,
 *   due Jun 15 — Apr 15 when the owner had US wages.
 * - The extended date applies ONLY when an admin approved the customer's
 *   extension proof (`extension.approvedAt`, decision D4). It is never assumed
 *   because the unextended date has passed — a passed date is overdue.
 * - That proof is a Form 7004, which extends the entity forms (1120 / 5472,
 *   1065) only. The 1040-NR needs Form 4868, which we do not accept, so it
 *   keeps its original date: an approved extension moves an SMLLC-with-ECI
 *   filing to the 1040-NR date, not to Oct 15.
 * - A due date on a Saturday, Sunday or DC Emancipation Day (as observed) rolls
 *   to the next business day. Due dates are UTC calendar dates; "today" is the
 *   IRS's calendar day, U.S. Eastern (`irsCalendarDate`, owner decision C2).
 * - A stored `deadlineDate` / `dueDate` is used only when no scenario (or no
 *   tax year) resolves. With neither, the deadline is `unknown`: `isOverdue`
 *   and `daysLeft` are null, never a reassuring `false`.
 */
import {
  answersIndicateEci,
  entityKindOfScenario,
  normalizeScenario,
  type Scenario,
} from "./scenario";

export type FilingDeadlineSource = "computed" | "stored" | "unknown";
export type FilingDeadlineStatus =
  | "unknown"
  | "upcoming"
  | "overdue"
  /** A Form 7004 proof is still being checked; the date is the extended one. */
  | "extension_pending";

export interface FilingDeadline {
  /** `YYYY-MM-DD`, or null when unknown. */
  dueDate: string | null;
  source: FilingDeadlineSource;
  isExtended: boolean;
  /** Calendar days from today (U.S. Eastern, `irsCalendarDate`) to the due date; negative once passed. */
  daysLeft: number | null;
  isOverdue: boolean | null;
  /**
   * The form whose date `dueDate` is (the earliest of the scenario's forms).
   * Only when the date was computed from the scenario, never for a stored or
   * unknown one. Lets a screen say "Form 1040-NR" when it binds.
   */
  dueForm?: DeadlineForm;
  /**
   * Set only by `resolveFilingDeadlineForReview`, and only while a Form 7004
   * proof is still pending AND an entity form (5472, 1120, 1065) is the
   * binding date: `dueDate` is then the extended date and `isExtended` stays
   * false (nobody approved it yet). An SMLLC-with-ECI's 1040-NR is never
   * extended by a 7004, so when it binds this stays unset.
   */
  extensionPending?: boolean;
}

export type DeadlineForm = "f5472" | "f1120" | "f1040nr" | "f1065";

export interface FormDeadline {
  form: DeadlineForm;
  /** `YYYY-MM-DD` */
  dueDate: string;
}

type MonthDay = { month: number; day: number };

const DAY_MS = 24 * 60 * 60 * 1000;

const DUE: Record<DeadlineForm, { base: MonthDay; extended: MonthDay }> = {
  f5472: { base: { month: 4, day: 15 }, extended: { month: 10, day: 15 } },
  f1120: { base: { month: 4, day: 15 }, extended: { month: 10, day: 15 } },
  f1040nr: { base: { month: 6, day: 15 }, extended: { month: 10, day: 15 } },
  f1065: { base: { month: 3, day: 15 }, extended: { month: 9, day: 15 } },
};

const SCENARIO_FORMS: Record<Scenario, DeadlineForm[]> = {
  smllc_no_eci: ["f5472"],
  smllc_eci: ["f5472", "f1040nr"],
  ccorp: ["f1120"],
  mmllc: ["f1065"],
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return null;
}

/**
 * DC Emancipation Day as observed: April 16, or Friday April 15 when the 16th
 * is a Saturday, or Monday April 17 when it is a Sunday.
 */
function isEmancipationDayObserved(date: Date): boolean {
  const year = date.getUTCFullYear();
  const holiday = new Date(Date.UTC(year, 3, 16));
  const weekday = holiday.getUTCDay();
  if (weekday === 6) holiday.setUTCDate(15);
  else if (weekday === 0) holiday.setUTCDate(17);
  return date.getUTCMonth() === 3 && date.getUTCDate() === holiday.getUTCDate();
}

/** Moves a date off weekends and observed Emancipation Day, in UTC. */
export function rollToBusinessDay(date: Date): Date {
  const next = new Date(date.getTime());
  while (
    next.getUTCDay() === 0 ||
    next.getUTCDay() === 6 ||
    isEmancipationDayObserved(next)
  ) {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  return next;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Spec 7.4: `hasExtension` is `extension?.approvedAt != null`, read strictly:
 * the approval writer stores a timestamp string, so anything else (`true`,
 * `""`, a number) is not an approval and never moves the deadline.
 */
export function hasApprovedExtension(
  serviceData: Record<string, unknown>,
): boolean {
  const approvedAt = asRecord(serviceData.extension)?.approvedAt;
  return typeof approvedAt === "string" && approvedAt.trim() !== "";
}

function qualificationAnswers(
  serviceData: Record<string, unknown>,
): Record<string, unknown> | null {
  return asRecord(asRecord(serviceData.qualification)?.answers);
}

/** The qualification's `eci.us_wages` answer moves the 1040-NR to April 15. */
export function hasUsWages(serviceData: Record<string, unknown>): boolean {
  return qualificationAnswers(serviceData)?.["eci.us_wages"] === true;
}

/** Legacy wizard screening: any "yes" among its answers meant ECI. */
function legacyScreeningHasEci(serviceData: Record<string, unknown>): boolean {
  const screening = asRecord(asRecord(serviceData.filing)?.screeningData);
  const questions = Array.isArray(screening?.questions)
    ? screening.questions
    : [];
  return questions.some((question) => asRecord(question)?.answer === "yes");
}

/**
 * The order's scenario: `scenario`, `filing.scenario`, `qualification.scenario`,
 * then the docgen scenario (what DocuSeal-era forms were built for). A bare
 * `smllc` splits on the qualification's ECI answers, else the legacy screening.
 */
export function resolveFilingScenario(
  serviceData: Record<string, unknown>,
): Scenario | null {
  const answers = qualificationAnswers(serviceData);
  const hasEci = answers
    ? answersIndicateEci(answers)
    : legacyScreeningHasEci(serviceData);
  const candidates = [
    firstText(serviceData.scenario, asRecord(serviceData.filing)?.scenario),
    firstText(asRecord(serviceData.qualification)?.scenario),
    firstText(asRecord(serviceData.federalTaxDocgenV2)?.scenario),
  ];
  for (const candidate of candidates) {
    const resolved = normalizeScenario(candidate, { hasEci });
    if (resolved) return resolved;
  }
  return null;
}

/** `taxYear` / `tax_year` as a number or numeric string; never guessed. */
export function resolveFilingTaxYear(
  serviceData: Record<string, unknown>,
): number | null {
  for (const raw of [serviceData.taxYear, serviceData.tax_year]) {
    const parsed =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
          ? Number.parseInt(raw, 10)
          : Number.NaN;
    if (Number.isInteger(parsed) && parsed > 1900 && parsed < 3000)
      return parsed;
  }
  return null;
}

/** Each form's due date for the scenario, earliest first. */
export function formDeadlines(
  scenario: Scenario,
  taxYear: number,
  options: { extended: boolean; usWages: boolean },
): FormDeadline[] {
  return SCENARIO_FORMS[scenario]
    .map((form) => {
      const rule = DUE[form];
      // The only extension we accept is Form 7004, which covers the entity
      // forms (f7004 Rev. Dec 2025, line 1 codes: 1065 = 09, 1120 = 12,
      // https://www.irs.gov/pub/irs-pdf/f7004.pdf). The 1040-NR is extended by
      // Form 4868 only (f4868 2025, p. 1 "Purpose of Form",
      // https://www.irs.gov/pub/irs-pdf/f4868.pdf), so it keeps its date.
      const extended = options.extended && form !== "f1040nr";
      let { month, day } = extended ? rule.extended : rule.base;
      // i1040nr 2025 p. 11 "When and Where Should You File?": Apr 15 with
      // wages subject to withholding, Jun 15 without.
      if (form === "f1040nr" && options.usWages) {
        month = 4;
        day = 15;
      }
      const due = rollToBusinessDay(
        new Date(Date.UTC(taxYear + 1, month - 1, day)),
      );
      return { form, dueDate: isoDate(due) };
    })
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

/** A stored date string as a UTC calendar date, when it starts with one. */
function storedDueDate(serviceData: Record<string, unknown>): Date | null {
  const stored = firstText(
    serviceData.deadlineDate,
    serviceData.deadline_date,
    serviceData.dueDate,
    serviceData.due_date,
  );
  const match = stored ? /^(\d{4})-(\d{2})-(\d{2})/.exec(stored) : null;
  if (!match) return null;
  const [year, month, day] = [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
  ];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * The time zone whose calendar day decides "today" for every federal-tax
 * deadline (owner decision C2, 2026-10-14): a return is timely when its
 * postmark is on or before the due date (IRC 7502, Treas. Reg. 301.7502-1(c);
 * an electronic postmark is dated in the filer's time zone, 301.7502-1(d)(1)(ii)),
 * and the IRS day we file on ends at midnight U.S. Eastern, not 00:00 UTC.
 */
export const IRS_TIME_ZONE = "America/New_York";

const IRS_DAY_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: IRS_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * `now` as the IRS's calendar date, `YYYY-MM-DD` in America/New_York: 2026-04-15
 * 23:30 EDT (2026-04-16 03:30 UTC) is still `2026-04-15`. The one "today" for
 * filing phases, days left, overdue checks, reminders and filing dates.
 */
export function irsCalendarDate(now: Date): string {
  const parts = IRS_DAY_FORMAT.formatToParts(now);
  const part = (type: "year" | "month" | "day") =>
    parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** The calendar year of `irsCalendarDate(now)`. */
export function irsCalendarYear(now: Date): number {
  return Number(irsCalendarDate(now).slice(0, 4));
}

/** Calendar days from `now`'s IRS (Eastern) day to a `YYYY-MM-DD` date; negative once passed. */
function daysUntil(dueDate: string, now: Date): number {
  const today = Date.parse(`${irsCalendarDate(now)}T00:00:00.000Z`);
  return Math.round((Date.parse(`${dueDate}T00:00:00.000Z`) - today) / DAY_MS);
}

/** Calendar days from `now`'s IRS (Eastern) day to a `YYYY-MM-DD` date; null when it is not one. */
export function irsDaysUntil(dateOnly: string, now: Date): number | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) &&
    !Number.isNaN(Date.parse(`${dateOnly}T00:00:00.000Z`))
    ? daysUntil(dateOnly, now)
    : null;
}

export function resolveFilingDeadline(
  serviceData: Record<string, unknown>,
  now: Date,
): FilingDeadline {
  const isExtended = hasApprovedExtension(serviceData);
  const scenario = resolveFilingScenario(serviceData);
  const taxYear = resolveFilingTaxYear(serviceData);

  let dueDate: string | null = null;
  let dueForm: DeadlineForm | undefined;
  let source: FilingDeadlineSource = "unknown";
  if (scenario && taxYear !== null) {
    const earliest = formDeadlines(scenario, taxYear, {
      extended: isExtended,
      usWages: hasUsWages(serviceData),
    })[0];
    if (earliest) {
      dueDate = earliest.dueDate;
      dueForm = earliest.form;
      source = "computed";
    }
  }
  if (!dueDate) {
    const stored = storedDueDate(serviceData);
    if (stored) {
      dueDate = isoDate(stored);
      source = "stored";
    }
  }
  if (!dueDate) {
    return {
      dueDate: null,
      source: "unknown",
      isExtended,
      daysLeft: null,
      isOverdue: null,
    };
  }

  const daysLeft = daysUntil(dueDate, now);
  return {
    dueDate,
    ...(dueForm ? { dueForm } : {}),
    source,
    isExtended,
    daysLeft,
    isOverdue: daysLeft < 0,
  };
}

export interface FilingDeadlineDates {
  /** The due date without any extension, `YYYY-MM-DD`. */
  originalDate: string | null;
  /** The extended due date — only once an admin approved the extension proof. */
  extendedDate: string | null;
  /** What applies now: the extended date when approved, else the original. */
  effectiveDate: string | null;
  hasExtension: boolean;
}

/**
 * The three dates a screen shows side by side (the console's deadline block,
 * the customer's "Extension approved" line). An extended date is only ever
 * computed from the scenario; a stored date is never pushed out by guesswork.
 */
export function filingDeadlineDates(
  serviceData: Record<string, unknown>,
): FilingDeadlineDates {
  // Only `dueDate` is read below, and it does not depend on the clock.
  const epoch = new Date(0);
  const hasExtension = hasApprovedExtension(serviceData);
  const original = resolveFilingDeadline(
    { ...serviceData, extension: null },
    epoch,
  );
  const extended = hasExtension
    ? resolveFilingDeadline(serviceData, epoch)
    : null;
  const extendedDate =
    extended?.source === "computed" ? extended.dueDate : null;
  return {
    originalDate: original.dueDate,
    extendedDate,
    effectiveDate: extendedDate ?? original.dueDate,
    hasExtension,
  };
}

/** The explicit three-way status every read surfaces next to the date. */
export function filingDeadlineStatus(
  deadline: FilingDeadline,
): FilingDeadlineStatus {
  if (deadline.extensionPending) return "extension_pending";
  if (deadline.isOverdue === null) return "unknown";
  return deadline.isOverdue ? "overdue" : "upcoming";
}

// ---------------------------------------------------------------------------
// The customer's filing window: before the original date, between it and the
// Form 7004 extended date, or late (owner decision 2026-09-29). Both dates are
// rolled off weekends and holidays like every other due date here.
// ---------------------------------------------------------------------------

/** The billing kind of the entity (`EntityKind` in ./scenario). */
type WindowKind = "smllc" | "mmllc" | "ccorp";

const WINDOW_FORM: Record<WindowKind, DeadlineForm> = {
  smllc: "f5472",
  mmllc: "f1065",
  ccorp: "f1120",
};

export interface EntityFilingWindow {
  /** The entity return's due date without an extension, `YYYY-MM-DD`. */
  originalDate: string;
  /** The date once a Form 7004 was filed by the original date, `YYYY-MM-DD`. */
  extendedDate: string;
}

/**
 * The entity return's original and Form 7004 extended due dates for a tax year
 * (Form 5472 / 1120: Apr 15 -> Oct 15; Form 1065: Mar 15 -> Sep 15).
 */
export function entityFilingWindow(
  kind: WindowKind,
  taxYear: number,
): EntityFilingWindow {
  const rule = DUE[WINDOW_FORM[kind]];
  const roll = ({ month, day }: MonthDay) =>
    isoDate(rollToBusinessDay(new Date(Date.UTC(taxYear + 1, month - 1, day))));
  return {
    originalDate: roll(rule.base),
    extendedDate: roll(rule.extended),
  };
}

export type FilingPhase = "standard" | "extension_window" | "late";

export interface FilingPhaseResult extends EntityFilingWindow {
  phase: FilingPhase;
}

/**
 * Where `now` (its IRS calendar day, `irsCalendarDate`) sits for this tax year: `standard` through
 * the original date, `extension_window` from the next day through the extended
 * date, `late` after it. Both edges include the due date itself.
 */
export function filingPhase(
  kind: WindowKind,
  taxYear: number,
  now: Date,
): FilingPhaseResult {
  const window = entityFilingWindow(kind, taxYear);
  const today = irsCalendarDate(now);
  const phase: FilingPhase =
    today <= window.originalDate
      ? "standard"
      : today <= window.extendedDate
        ? "extension_window"
        : "late";
  return { phase, ...window };
}

// ---------------------------------------------------------------------------
// A Form 7004 proof still being checked. The order started on the extension
// path (the normal fee was taken after the original date on the customer's
// claim), so until the console decides, the deadline that counts is the
// extended one "if accepted" — never "past due". Every screen that would call
// the stored (original) date past due asks here first.
// ---------------------------------------------------------------------------

export type ExtensionReviewState = "pending" | "approved" | "rejected";

/**
 * Where the extension proof of an order that STARTED on the extension path
 * stands: `approved` (`approvedAt`), `rejected` (a reason was given), else
 * `pending`. Null for any other order, including a post-order upload that is
 * only being checked (its original date has not been called past due).
 */
export function extensionReviewState(
  serviceData: Record<string, unknown>,
): ExtensionReviewState | null {
  const extension = asRecord(serviceData.extension);
  if (extension?.onExtensionPath !== true) return null;
  if (hasApprovedExtension(serviceData)) return "approved";
  if (firstText(extension.rejectedReason) !== null) return "rejected";
  return "pending";
}

/**
 * The original and extended dates while the proof is pending and the extended
 * date has not passed (`now`'s IRS calendar day, inclusive). Null when there is nothing
 * to wait on: approved, rejected, past the extended date, or no scenario or
 * tax year to compute the dates from.
 */
export function pendingExtensionWindow(
  serviceData: Record<string, unknown>,
  now: Date,
): EntityFilingWindow | null {
  if (extensionReviewState(serviceData) !== "pending") return null;
  const scenario = resolveFilingScenario(serviceData);
  const taxYear = resolveFilingTaxYear(serviceData);
  if (!scenario || taxYear === null) return null;
  const window = entityFilingWindow(entityKindOfScenario(scenario), taxYear);
  return irsCalendarDate(now) <= window.extendedDate ? window : null;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * `2026-04-15` -> `April 15, 2026` (a longer timestamp reads its date prefix);
 * anything without a `YYYY-MM-DD` prefix and a real month comes back as it is.
 */
export function longDate(isoDay: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDay);
  const month = match ? MONTH_NAMES[Number(match[2]) - 1] : undefined;
  return match && month ? `${month} ${Number(match[3])}, ${match[1]}` : isoDay;
}

/** The line every screen shows for a pending proof, e.g. `2026-10-15` in it. */
export function extensionUnderReviewCopy(extendedDate: string): string {
  return `Form 7004 under review — due ${longDate(extendedDate)} if accepted`;
}

/**
 * The deadline an admin read reports: `resolveFilingDeadline`, except that
 * while a Form 7004 proof is pending (`pendingExtensionWindow`) the date is the
 * earliest of the scenario's forms AS IF the proof were accepted
 * (`formDeadlines` with `extended: true`, exactly what an approval would
 * yield). When an entity form (5472, 1120, 1065) is that earliest date it is
 * the extended one, the filing is not overdue and `extensionPending` is true.
 * When it is an SMLLC-with-ECI's 1040-NR, which a Form 7004 never extends, the
 * 1040-NR date counts as itself (Jun 15, Apr 15 with US wages): upcoming or
 * overdue like any other, never "under review", with `dueForm` naming it.
 * Approved and rejected proofs, and any date past the extended one, fall back
 * to `resolveFilingDeadline` unchanged (approved extends, rejected is overdue).
 */
export function resolveFilingDeadlineForReview(
  serviceData: Record<string, unknown>,
  now: Date,
): FilingDeadline {
  const window = pendingExtensionWindow(serviceData, now);
  if (!window) return resolveFilingDeadline(serviceData, now);
  // `pendingExtensionWindow` only answers with a resolved scenario and year.
  const scenario = resolveFilingScenario(serviceData)!;
  const taxYear = resolveFilingTaxYear(serviceData)!;
  const earliest = formDeadlines(scenario, taxYear, {
    extended: true,
    usWages: hasUsWages(serviceData),
  })[0]!;
  const daysLeft = daysUntil(earliest.dueDate, now);
  const entityBinds = earliest.form !== "f1040nr";
  return {
    dueDate: earliest.dueDate,
    dueForm: earliest.form,
    source: "computed",
    isExtended: false,
    daysLeft,
    isOverdue: daysLeft < 0,
    ...(entityBinds ? { extensionPending: true } : {}),
  };
}
